// packages/web-ui/src/AppShell/ContextPanel.tsx
//
// W2-S3 (AC #5/#6) — populated ContextPanel. Reads `activeDestination` from
// the navigation slice and renders the matching mode body
// (`ChatMode` / `FilesMode` / `KbMode` / `ViewsMode` / `DiffMode` /
// `SettingsCollapsed`). Mode swap honours `prefers-reduced-motion: reduce`.
//
// The component preserves the W2-S2 props contract — `width`, `collapsed`,
// `mode`, `breakpoint`, `activeDestination` — so the AppShell parent does
// not need restructuring. Settings forces the panel to 0 px width via the
// `collapsed` prop set by AppShell.

import { useCallback, useEffect, useState } from 'react';
import type { JSX, CSSProperties, ComponentType } from 'react';
import { Z_INDEX, type Breakpoint } from './responsive-tokens.js';
import type { ActiveDestination } from '../state/navigation.js';
import { closeDeletedSessionTabs } from '../state/close-session-tabs.js';
import { ChatMode } from './ContextPanel-modes/ChatMode.js';
import { FilesMode } from './ContextPanel-modes/FilesMode.js';
import { KbMode } from './ContextPanel-modes/KbMode.js';
import { ViewsMode } from './ContextPanel-modes/ViewsMode.js';
import { DiffMode } from './ContextPanel-modes/DiffMode.js';
import { GitMode } from './ContextPanel-modes/GitMode.js';
import { SettingsCollapsed } from './ContextPanel-modes/SettingsCollapsed.js';
import { SkillsCollapsed } from './ContextPanel-modes/SkillsCollapsed.js';
import { useReducedMotion } from './useReducedMotion.js';
import { sessionsStoreSingleton as sessionsStore } from '../state/sessions-store-singleton.js';
import { useRootStore } from '../state/store.js';
import { navigateTo } from '../router/hash-router.js';

// `sessionsStore` is a module-level singleton (shared with TabContent's
// chat auto-selector). It survives ContextPanel re-mounts and the
// destination cross-fade swap. The slice is intentionally standalone
// (see `state/sessions.ts` header) so it lives outside the root store.

export type ContextPanelMode = 'inline' | 'overlay' | 'hidden';

export interface ContextPanelProps {
  width: number;
  collapsed: boolean;
  mode: ContextPanelMode;
  breakpoint: Breakpoint;
  activeDestination: ActiveDestination;
}

const DESTINATION_ARIA_LABEL: Record<ActiveDestination, string> = {
  chat: 'Chat sessions index',
  kb: 'KB index',
  files: 'Files index',
  views: 'Views index',
  settings: 'Settings index',
  diff: 'Diff index',
  git: 'Git index',
  skills: 'Skills index',
};

/**
 * Lookup table for stateless modes — Pitfall: `if/else` chains are forbidden
 * by Task 6.1. The `chat` destination is rendered separately because it
 * needs to be wired to the sessions slice + hash router.
 */
const MODE_BODIES: Record<Exclude<ActiveDestination, 'chat'>, ComponentType> = {
  files: FilesMode,
  kb: KbMode,
  views: ViewsMode,
  diff: DiffMode,
  git: GitMode,
  settings: SettingsCollapsed,
  skills: SkillsCollapsed,
};

export function ContextPanel({
  width,
  collapsed,
  mode,
  breakpoint,
  activeDestination,
}: ContextPanelProps): JSX.Element {
  const reduced = useReducedMotion();
  const effectiveWidth = collapsed ? 0 : width;

  // Cross-fade swap: track the destination we last rendered. On change, fade
  // out for one frame then fade back in. Skipped under reduced motion.
  const [renderedDestination, setRenderedDestination] = useState(activeDestination);
  const [opacityClass, setOpacityClass] = useState('opacity-100');

  useEffect(() => {
    if (renderedDestination === activeDestination) return;
    if (reduced) {
      setRenderedDestination(activeDestination);
      setOpacityClass('opacity-100');
      return;
    }
    // Frame 1: fade out.
    setOpacityClass('opacity-0');
    const handle = window.setTimeout(() => {
      setRenderedDestination(activeDestination);
      setOpacityClass('opacity-100');
    }, 16);
    return () => {
      window.clearTimeout(handle);
    };
  }, [activeDestination, renderedDestination, reduced]);

  // The collapse-to-0 width animation lives on the container's inline style.
  const transitionStyle: CSSProperties = reduced
    ? {}
    : { transition: 'width 200ms ease-out' };

  const style: CSSProperties =
    mode === 'overlay'
      ? {
          width: effectiveWidth,
          zIndex: Z_INDEX.modal,
          ...transitionStyle,
        }
      : { width: effectiveWidth, zIndex: Z_INDEX.baseCanvas, ...transitionStyle };

  const activeSessionId = useRootStore((s) => s.activeSessionId);
  const handleActivateSession = useCallback((sessionId: string) => {
    if (sessionId === '') {
      // Defensive: a caller signalling "no active session" via an empty
      // string drops the chat tab back to the unparameterised /chat route.
      navigateTo({ kind: 'chat' });
      return;
    }
    navigateTo({ kind: 'chat', sessionId });
  }, []);

  // When sessions are deleted, close their `chat/<sessionId>` tabs so a
  // removed conversation never leaves an orphaned tab behind (see
  // `closeDeletedSessionTabs` for the active-session reconciliation).
  const handleSessionsDeleted = useCallback((sessionIds: string[]) => {
    closeDeletedSessionTabs(sessionIds);
  }, []);

  const Body = renderedDestination === 'chat' ? null : MODE_BODIES[renderedDestination];

  return (
    <aside
      role="complementary"
      aria-label={DESTINATION_ARIA_LABEL[activeDestination]}
      data-testid="context-panel"
      data-mode={mode}
      data-collapsed={collapsed ? 'true' : 'false'}
      data-breakpoint={breakpoint}
      data-destination={activeDestination}
      data-rendered-destination={renderedDestination}
      className="bg-cream-100 border-r border-taupe-300 overflow-hidden"
      style={style}
    >
      <div
        data-testid="context-panel-fade-layer"
        className={
          reduced
            ? 'h-full w-full opacity-100'
            : `h-full w-full transition-opacity duration-150 ${opacityClass}`
        }
      >
        {Body !== null ? (
          <Body />
        ) : (
          <ChatMode
            sessionsStore={sessionsStore}
            activeSessionId={activeSessionId}
            onActivateSession={handleActivateSession}
            onSessionsDeleted={handleSessionsDeleted}
          />
        )}
      </div>
    </aside>
  );
}
